import { debounce } from 'lodash';
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type ReactNode, type Ref } from 'react';

import { useBreakpoint } from '@hivebrite/design-system/Theme';

import { shorthandParser } from 'hb-builder/utils/attributes';
import { getNumberOfNestedChildrenInSection } from 'hb-builder/utils/getNumberOfNestedChildrenInSection';
import { type DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import { type BodyItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/bodyItemAttributes';
import { type SectionItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/sectionItemAttributes';

interface Context {
  attributes?: SectionItemAttributes;
  bodyWidth?: BodyItemAttributes['bodyWidth'];
  calculateSectionHeight: () => number | undefined;
  nbFinalChildren: number;
  sectionHeight?: number;
  sectionWidth?: number;
}

export interface SectionProviderChildrenProps
  extends Omit<Context, 'attributes' | 'bodyWidth' | 'calculateSectionHeight'> {
  ref: Ref<HTMLDivElement>;
}

const SectionContext = createContext<Context>({
  calculateSectionHeight: () => undefined,
  nbFinalChildren: 0,
});

interface SectionProviderProps {
  bodyWidth?: BodyItemAttributes['bodyWidth'];
  centerWidth?: number;
  children: (args: SectionProviderChildrenProps) => ReactNode;
  item: DocumentItem<SectionItemAttributes>;
}

export const SectionProvider = ({
  bodyWidth,
  centerWidth,
  children,
  item,
}: SectionProviderProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const breakpoint = useBreakpoint();

  const [sectionHeight, setSectionHeight] =
    useState<Context['sectionHeight']>();
  const [sectionWidth, setSectionWidth] = useState<Context['sectionWidth']>();

  const stringItem = useMemo(() => JSON.stringify(item), [item]);

  const nbFinalChildren = useMemo(
    () => getNumberOfNestedChildrenInSection(item),
    [item],
  );

  const triggerChange = debounce((section) => {
    if (!section) {
      return null;
    }

    const { height, width } = section.getBoundingClientRect();

    const finalHeight =
      height -
      shorthandParser(item?.attributes?.containerPadding, 'top') -
      shorthandParser(item?.attributes?.containerPadding, 'bottom');

    setSectionHeight(finalHeight);
    setSectionWidth(width);
  }, 200);

  useLayoutEffect(() => {
    setSectionHeight(undefined);
    triggerChange(ref.current);

    return () => {
      triggerChange.cancel();
    };
  }, [breakpoint, centerWidth, stringItem]);

  const calculateSectionHeight = useCallback(() => {
    triggerChange(ref.current);

    return sectionHeight;
  }, [sectionHeight, triggerChange]);

  const value = useMemo(
    () => ({
      attributes: item.attributes,
      bodyWidth,
      calculateSectionHeight,
      nbFinalChildren,
      sectionHeight,
      sectionWidth,
    }),
    [
      bodyWidth,
      calculateSectionHeight,
      item,
      nbFinalChildren,
      sectionHeight,
      sectionWidth,
    ],
  );

  return (
    <SectionContext.Provider value={value}>
      {children({
        nbFinalChildren,
        sectionHeight,
        ref,
        sectionWidth,
      })}
    </SectionContext.Provider>
  );
};

export const useSectionContext = () => {
  const context = useContext(SectionContext);

  if (!context) {
    throw new Error('useSectionContext must be used within a SectionProvider');
  }

  return context;
};
